import { OneMapApiDataService } from '../model/OneMapApiDataService';
import { useState, useEffect, useContext, useCallback } from 'react';
import { InterfaceContext } from '../model/InterfaceProvider';
import MapContext from '../controller/MapProvider';
import { staticPOIList } from '../model/StaticVariables';
import CircleFeature, { PoiBubble, bubbleZoomSizeMatrix } from './CircleFeature';
import * as DT from '../model/DataTransformation';
import { VectorLayer } from './Layer';
import { DevDataService } from '../model/DevDataService';
import { convertGeometryToFeatures } from '../controller/LayerQueries';
import * as POI from '../model/POI_model';
import * as DM from '../model/DistanceMap';

var state_data_cache = false;
var cached_poilookups = {};

const PoiLayer = (props) => {
    const { map, type, minZoom, onLoadStatus = () => {}, zIndex } = props;
    const context = useContext(InterfaceContext);
    const { year } = context;
    const { tiles, zoomRead: zoom } = useContext(MapContext);
    const visible = context[type];
    const [features, setFeatures] = useState([]);
    const title = 'poi_' + type;
    const [loadStatus, setLoadStatus] = useState(1);
    const poiInfo = staticPOIList.find((poi) => poi.slug === type);
    const DS = new OneMapApiDataService();
    const DDS = new DevDataService();

    // Create POI cache for type
    if (!(type in cached_poilookups)) {
        cached_poilookups[type] = {};
    }
    const loadStateGeometry = async () => {
        if (state_data_cache) return state_data_cache;
        var json = await DDS.getGeometryData({ scope: 'state' });
        state_data_cache = convertGeometryToFeatures(json[0], 'topojson');
    };
    const getStateGeometry = (ST) => {
        if (!ST) return false;
        return state_data_cache.find((f) => f.get('STUSPS') === ST);
    };

    const poiDataLookups = {
        tiles: async () => {
            return await fetchGeometry({ scope: type, tilesObj: tiles });
        },
        tileListFilteredByDistanceMap: async (distanceMap) => {
            var useIDs = POI.getAllIdsFromDistanceMap(distanceMap);
            const tileLookupData = await fetchGeometry({ scope: type, tilesObj: tiles });
            return POI.reduceTileLookupData(tileLookupData, (poi) => {
                return useIDs.has(poi.ID) && poi;
            });
        },
        poiListFilterdBuDistanceMap: async (distanceMap) => {
            var useIDs = POI.getAllIdsFromDistanceMap(distanceMap);
            var tileLookupData = await fetchGeometry({ scope: type, tilesObj: tiles });
            return tileLookupData
                .map((tile) => tile.list)
                .flat()
                .filter((row) => useIDs.has(row.ID));
        }
    };

    const generateFeatures = async () => {
        var tileLookupData = await fetchGeometry({ scope: type, tilesObj: tiles });
        const formattedPoiData = formatGeometry(tileLookupData);
        const features = poiClusterToFeatures(formattedPoiData);
        return features;
    };

    //##@@
    const generateFeaturesMergeUsingDistanceMap = async () => {
        const distances = await DM.loadSqlDistanceMap(type);
        if (!cached_poilookups) {
            // cached_poilookups = await fetchGeometry({ scope: type, tilesObj: tiles });
            cached_poilookups = await poiDataLookups.tileListFilteredByDistanceMap(distances);
        }
        var tileLookupData = cached_poilookups;
        const formattedPoiData = formatGeometryWithDistanceMap(tileLookupData, distances);
        console.log('formatteddata', formattedPoiData);
        const features = poiClusterToFeatures(formattedPoiData);
        return features;
    };

    const pointIsWithinUsState = (point) => {
        var states = point.data.reduce((list, p) => {
            list.add(p.STATE);
            return list;
        }, new Set());
        return Array.from(states).find((s) => {
            var stateFeature = getStateGeometry(s);
            if (!stateFeature) return false;
            var stgeo = stateFeature.getGeometry();
            var isIntersecting = stgeo.intersectsCoordinate([point.x, point.y]);
            return isIntersecting;
        });
    };
    const movePointToState = (point) => {
        var stategeo = getStateGeometry(point.data[0].STATE).getGeometry();
        var newCenter = stategeo.getClosestPoint([point.x, point.y]);
        point.x = newCenter[0];
        point.y = newCenter[1];
        return point;
    };
    const preventPointsFromBeingInWater = (points) => {
        // for now we are going to loop through all US states;
        // ideally use a single US feature
        const keepPointOnDryLand = (point) => {
            const stateWithin = pointIsWithinUsState(point);
            if (stateWithin) return point;
            return movePointToState(point);
        };
        return points.map((point) => keepPointOnDryLand(point));
    };

    const refreshLayer = async () => {
        if (!map || !Object.keys(tiles).length || loadStatus === 0) return;
        setLoadStatus(0);
        const features = await generateFeatures();
        // const features = await generateFeaturesDistMapSimple();
        // const features = await generateFeaturesDistMap();
        // const features = await generateFeaturesDistMapDevVersion();
        // const features = await generateFeaturesFromSqlDistanceMaps();
        // const features = await generateFeaturesMergeUsingDistanceMap();
        setFeatures(features);
        setLoadStatus(1);
    };

    const poiClusterToFeatures = (poiClusters = []) => {
        if (!poiClusters.length) return [];

        return poiClusters.reduce((output, poiGroup) => {
            if (poiGroup.data.length) {
                const { x, y, data } = poiGroup;
                output.push(PoiBubble({ x, y, data, poiInfo, map }));
            }
            return output;
        }, []);
    };
    const mergePointsByPixel = (clusters, pixelRange = 20) => {
        //expect input [{x,y,data:[]}]
        let restClusters = [...clusters];
        let clusterA = restClusters.pop();
        let loop = 0;
        const boundary = 1000;
        const outputClusters = [];

        while (clusterA && loop < boundary) {
            let clusterResults = separatePointsByRange(clusterA, restClusters, pixelRange);
            let merged = mergeClusters([clusterA, ...clusterResults[0]]);
            outputClusters.push(merged);
            restClusters = clusterResults[1];
            clusterA = restClusters.pop();
            loop++;
        }
        return outputClusters;
    };
    //merge by pixel, loops through clusters (tiles made to clusters) works recusively
    //uses separatePointsByRange => is actually separate clusters by range
    //mergeClusters => just taking points and putting together no distance matching
    //##@@
    const mergePointsByDistanceMap = (clusters, pixelRange, distances) => {
        //expect input [{x,y,data:[]}]
        let restClusters = [...clusters.slice(0, 1000)];
        let clusterA = restClusters.pop();
        let loop = 0;
        const boundary = clusters.length;
        const outputClusters = [];
        console.log('=======================================');
        console.log('>>> Start merge: cluster#', restClusters.length);
        console.time('merge');
        while (clusterA && loop < boundary) {
            let clusterResults = findClustersInOutOfRange(clusterA, restClusters, pixelRange, distances);
            let merged = mergeClusters([clusterA, ...clusterResults[0]]);
            outputClusters.push(merged);
            restClusters = clusterResults[1];
            clusterA = restClusters.pop();
            loop++;
        }
        console.log('>>> End Merge');
        console.timeEnd('merge');
        // console.log('max loop', loop);
        return outputClusters;
    };
    //##@@
    const getMeterRange = (pixelRange, map) => map.getView().getResolution() * pixelRange;

    const findClustersInOutOfRange = (cluster, rest, pixelRange, distances, skipsql = false) => {
        /*
        fn goal: cluster is a cluster, rest is a cluster, 
        compare the distances between cluster and each item in rest
        sort the items in rest into 2 arrays, within and without
        return within and without

    */
        let within = [];
        let without = [];
        let withinDist = 0;
        let meterRange = getMeterRange(pixelRange, map);
        console.log('zoom', Math.floor(map.getView().getZoom()), 'meter range', meterRange);
        const pAID = cluster.data[0].ID;
        const lineHasPoint = (ln, pt) => ln.pointA === pt || ln.pointB === pt;
        const linesWithA = distances.filter((ln) => lineHasPoint(ln, pAID));
        // console.log('Lines with A', pAID, linesWithA);

        /*
            need to compare distance calclations here.
            - all matching points when using distance map are outside of the range
            - this doesn't happen for pixel comparisons
            - if we didn't use pixel ranges would we get the same results?
            - lets take some samples and
                0. compare results using non-square root and range * range vs square root + range
                1. compare pixel distances to meter distances, see how many are in vs out, check IDS
                2. compare meter distances from DT fn to the results in distance map
                3. Filter inital data using distance map mathing IDs only
        */

        for (var i = 0; i < rest.length; i++) {
            let pointB = rest[i];
            let pBID = rest[i].data[0].ID;

            // let matchingLine = false;
            // let matchingLine = linesWithA.find((ln) => lineHasPoint(ln, pBID));

            const pointIsWithinRangeInDistanceMap = (point, range, distanceMap) => {
                let matchingLine = distanceMap.find((ln) => lineHasPoint(ln, pBID));
                //sql generated distances are sqrt so range is not multiplied by itself
                return matchingLine.DIST < range;
            };

            let matchingLine = linesWithA.find((ln) => lineHasPoint(ln, pBID));

            // if (false) {
            if (matchingLine && !skipsql) {
                // let sqlD = Math.floor(matchingLine.DIST);
                // let sqlP = matchingLine.DIST * matchingLine.DIST;
                // let d = DT.distanceBetweenPoints([cluster.x, cluster.y], [pointB.x, pointB.y]);
                // // let D = DT.distanceBetweenPoints([cluster.x, cluster.y], [pointB.x, pointB.y], true);
                // // console.log('Calc D SQRT', D, 'nonsqrt', d, 'Pre-genD', matchingLine.DIST);
                // // console.log('Calc d', d, 'SQL d', sqlD, 'diff', d - sqlD, 'ratio', d / sqlD, 'Sql**', sqlP);
                if (matchingLine.DIST < meterRange) {
                    within.push(pointB);
                    // withinDist++;
                } else {
                    without.push(pointB);
                }
            } else {
                if (DT.pointsAreWithinRange([cluster.x, cluster.y], [pointB.x, pointB.y], meterRange)) {
                    within.push(pointB);
                } else {
                    without.push(pointB);
                }
            }
        }
        // console.log('within', within.length, ' withinSQL', withinDist);
        // console.log('within', within.length);
        // console.log('mathcing', matching);
        // console.log('**********************************');
        return [within, without];
    };
    /*
    Results of distance comparrisons
    - when using square root method ie range is not multiplied by itself but sqrt is used, it's somewhat slower but not more accurate. Using range * itself is better
    - when using meters, out of 288 comparisons 207 clusters had the same number of points regardless of method of calc 81 were different
        - 43 clusters had more using meter calcs 
        - 38 clusters had more using pixel calcs
        - 
        //without sqrt 1.2sec
            //with sqrt 1.8sec
            //288 loops

    Results comparing calulations vs distance map
    - distances are very different when running calculations, but maybe that is because of the way that is it being called recusively? though it looks like its not calling recursively
    - even when comparing ranges, taking into account the sqrt vs pow issue it seems like the dstances are very different

    - The odd mismatch from above is because of a code type where pointA was not being matched, so it was just looking at point B
    - However the next issue is that when pulling all IDs from the distance map, not all those ids seem to match to each other
    
    
    */

    const getPixelCoordsFromCluster = (c) => {
        return map.getPixelFromCoordinate([c.x, c.y]).map((i) => Math.floor(i));
    };
    const mergeClusters = (clusters) => {
        const data = clusters.reduce((compiled, clust) => {
            return [...compiled, ...clust.data];
        }, []);
        const { x, y } = clusters[0];
        return { x, y, data }; //empty for now
    };

    const separatePointsByRange = (pointA, rest, range) => {
        let within = [];
        let without = [];
        let ca = getPixelCoordsFromCluster(pointA);
        let all = [];
        for (var i = 0; i < rest.length; i++) {
            let pointB = rest[i];
            let rrange = range * range;
            let cb = getPixelCoordsFromCluster(pointB);
            let d = DT.distanceBetweenPoints(ca, cb);
            if (d < rrange) {
                cb.push(1);
                within.push(pointB);
            } else {
                without.push(pointB);
            }
            all.push(cb);
        }

        return [within, without];
    };

    const groupSplit = (data, size) => {
        var statememo = {};
        data.map((tilePoiData) => DT.groupPoiByState(tilePoiData.list, statememo, 'object'));
        var output = [];
        for (let st of Object.keys(statememo)) {
            var chunks = DT.chunkArray(statememo[st].data, size);
            for (let i = 0; i < chunks.length; i++) {
                let chunk = chunks[i];
                let centers = chunk.map((c) => [c.x || c.geometry.x, c.y || c.geometry.y]);
                let center = DT.findCenter(centers);

                var obj = {
                    x: center[0],
                    y: center[1],
                    data: chunk
                };
                output.push(obj);
            }
        }
        return output;
    };

    const separatePoiDataByZoom = (data) => {
        const zoom = Math.floor(map.getView().getZoom());
        const chunksize = bubbleZoomSizeMatrix[zoom].subdivide;
        if (chunksize !== 1) {
            // var split = separatePreClustersByState(data, chunk);
            // var splitClusters = convertPreClustersToClusters(split);
            // return splitClusters;
            return groupSplit(data, chunksize);
        } else {
            return data.reduce((output, tilePoiData) => (output = [...output, ...DT.groupAsSingle(tilePoiData, 'array')]), []);
        }
    };
    //##@@
    const separatePoiData = (data) => {
        return data.reduce((output, tilePoiData) => (output = [...output, ...DT.groupAsSingle(tilePoiData, 'array')]), []);
    };

    const formatGeometry = (data) => {
        if (!data.length) return;
        let grouped = separatePoiDataByZoom(data);
        const merged = mergePointsByPixel(grouped, 40);
        return preventPointsFromBeingInWater(merged);
    };
    const getAllMeterRanges = (pixels = 40) => {
        const ranges = [];
        for (var zoom = 2; zoom < 15 + 1; zoom++) {
            var res = map.getView().getResolutionForZoom(zoom);
            var range = res * pixels;
            ranges.push({ zoom, range });
        }
        return ranges;
    };
    //##@@
    const formatGeometryWithDistanceMap = (data, distances) => {
        if (!data.length) return;
        //convert tiles to clusters
        let grouped = separatePoiData(data);
        console.log('view', map.getView());
        console.log('ranges', getAllMeterRanges());
        // console.log('merged', grouped); //5 tiles = 5 clusters
        const merged = mergePointsByDistanceMap(grouped, 40, distances);
        return preventPointsFromBeingInWater(merged);
    };

    const fetchPoiDataByExtent = async (key, bbox) => {
        const onMapExtent = { x1: bbox[0], y1: bbox[1], x2: bbox[2], y2: bbox[3] };
        if (key in cached_poilookups) {
            return cached_poilookups[type][key];
        }
        const data = await DS.getPOIData({ scope: type, extent: onMapExtent, year, tileKey: key, blacklist: poiInfo.blacklist }).then((res) => {
            return {
                list: res.map((item) => {
                    let {
                        geometry: { x, y },
                        attributes: { ID, NAME, ADDRESS, CITY, STATE, COUNTY, ZIP }
                    } = item;
                    return { x, y, ID, NAME, ADDRESS, CITY, STATE, COUNTY, ZIP };
                })
            };
        });
        cached_poilookups[type][key] = data;
        return data;
    };
    const fetchCoords = useCallback(
        async (usstate) => {
            return DS.getPOICoords({ scope: type, usstate }).then((res) => {
                return res.map((item) => {
                    let {
                        geometry: { x, y },
                        attributes: { ID, OBJECTID }
                    } = item;
                    return { ID: ID || OBJECTID, x, y };
                });
            });
        },
        [type]
    );

    const fetchGeometry = useCallback(async () => {
        const promises = Object.keys(tiles).map((key) => {
            return fetchPoiDataByExtent(key, tiles[key]);
        });
        await loadStateGeometry();
        return Promise.all(promises).then((res) => {
            return res.flat().filter((call) => call.list.length > 0);
        });
    }, [tiles, type]);

    const validateZoom = () => {
        let floorZoom = Math.floor(zoom);
        let tileZ = Object.keys(tiles)[0];
        let iz = tileZ?.indexOf('x');
        let zt = tileZ?.substring(2, iz);
        return floorZoom + '' === zt || floorZoom < minZoom;
    };

    useEffect(() => onLoadStatus(loadStatus), [loadStatus]);
    useEffect(async () => {
        if (!tiles || !visible) return;
        if (!map || !Object.keys(tiles).length || loadStatus === 0) return;
        if (!validateZoom()) return;
        refreshLayer();
    }, [tiles, visible, zoom]);

    return <VectorLayer title={title} className={'poi' + (visible ? ' active' : '')} features={features} zIndex={zIndex} opacity={0.8} visible={visible} map={map} />;
};
export default PoiLayer;
